import React, {useEffect, useState} from "react";

import APResultDialog from "../../components/APResultDialog";
import APCommonDialog from "../../components/APCommonDialog";
import InitialStep from "./components/InitialStep";
import ExerciseScreen from "./components/ExerciseScreen";
import Profile from "../Profile";
import {MAINSCREEN_RESULT_STEPS, MathProblem, OperatorModel, ValidationStatusModel} from "../../types";
import "../../App.css";
import {Box, useMediaQuery} from "@chakra-ui/react";
import {convertAnswerString, generateInitialProblems, getProblem, getProblemDifficulty} from "../../utils";
import {DndProvider} from 'react-dnd';
import {TouchBackend} from 'react-dnd-touch-backend';
import {HTML5Backend} from "react-dnd-html5-backend";
import APCorrectnessDialog from "../../components/APCorrectnessDialog";
import {INITIAL_DIFFICULTY, TAGS_LIST} from "../../constants";
import {FormattedMessage, useIntl} from "react-intl";
import TextWithLineBreaks from "../../components/TextWithLineBreaks";
import {useSettingsContext} from "../../contexts/SettingsContext";

function MainScreen({ sdk }: any) {
  const [initial, setInitial] = useState(true);
  const [demo, setDemo] = useState(false);
  const isTouchDevice = () => "ontouchstart" in document.documentElement;

  const [maxLevel, setMaxLevel] = useState("");
  const [currentOperation, setCurrentOperation] = useState("addition");
  const [problemsCount, setProblemsCount] = useState(0);

  const [currentDifficulty, setCurrentDifficulty] =
    useState(INITIAL_DIFFICULTY);
  const [problems, setProblems] = useState<MathProblem[]>([]);
  const [problemIndex, setProblemIndex] = useState(0);
  const [currentStep, setCurrentStep] = useState<number>(1);

  const [exitExercise, setExitExercise] = useState(false);
  const [result, setResult] = useState<MAINSCREEN_RESULT_STEPS>(
    MAINSCREEN_RESULT_STEPS.INITIAL
  );

  const [correct, setCorrect] = useState(0);
  const [correctProblems, setCorrectProblems] = useState<MathProblem[]>([]);
  const [wrongProblems, setWrongProblems] = useState<MathProblem[]>([]);
  const [nextProblem, setNextProblem] = useState<MathProblem | undefined>();
  const [wrongProblemsAdded, setWrongProblemsAdded] = useState(false);

  const [validationStatus, setValidationStatus] = useState<
    ValidationStatusModel | undefined
  >();

  //prevValidationStatus is for saving validation status because we are clearing validation
  // status when the problem changes
  const [prevValidationStatus, setPrevValidationStatus] = useState<
    ValidationStatusModel | undefined
  >();
  const [progress, setProgress] = useState(0);
  const [disableValidate, setDisableValidate] = useState(false);

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [sessionProblemsList, setSessionProblemsList] = useState<string[]>([]);
  const [sessionExerciseCount, setSessionExerciseCount] = useState<number>(0);
  const [annualProblemsList, setAnnualProblemsList] = useState<string[]>([]);
  const [annualExerciseCount, setAnnualExerciseCount] = useState<number>(0);

  const [exerciseInfo, setExerciseInfo] = useState<{
    level: string,
    operation: OperatorModel,
    difficulty: number,
    problemsLength: number
  }>();

  const [restartMode, setRestartMode] = useState(false);

  const {settings, onSetSettings} = useSettingsContext();
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");

  const intl = useIntl();

  useEffect(() => {
    const handleSessionData = async () => {
      try {
        if (sdk) {
          const sessionData = await sdk.getSessionManager().getSessionData();
          setSessionExerciseCount(sessionData.sessionExerciseCount || 0);
          setSessionProblemsList(sessionData.sessionWordList || []);
        }
      } catch(err) {}
    }
    if (sdk) {
      setIsLoggedIn(sdk._firebase.isLoggedIn());
      handleSessionData();
    }
  }, [sdk]);

  useEffect(() => {
    if (isLoggedIn) {
      const getUserSettings = async () => {
        try{
          const user = await sdk.getDataManager().getSettings();
          if (user && user.annualExerciseCount ) {
            setAnnualExerciseCount(user.annualExerciseCount);
          }
          if (user && user.annualProblemsList) {
            setAnnualProblemsList(user.annualProblemsList);
          }
        } catch(err) {}
      }
      getUserSettings();
    }
  }, [sdk, isLoggedIn]);

  const complete = () => {
    setResult(MAINSCREEN_RESULT_STEPS.INITIAL);
    setWrongProblems([]);
    setCorrectProblems([]);
    setCurrentStep(1);
    setProgress(0);
  };

  const updateAnnualProblemsList = (id: string) => {
    if (annualProblemsList.indexOf(id) === -1) {
      setAnnualProblemsList(annualProblemsList.concat(id));
    }
  };

  const updateSessionProblemsList = (id: string) => {
    if (sessionProblemsList.indexOf(id) === -1) {
      setSessionProblemsList(sessionProblemsList.concat(id));
    }
  };

  const onValidte = async (answerString: string) => {
    let tempStep = problemIndex,
      tempCorrect = correct;
    if (!problems || problems.length === 0) {
      return;
    }
    {
      tempStep = problemIndex;
      const currentProblem = problems[tempStep];
      const correctAnswer = parseFloat(
        (currentProblem.operator === OperatorModel.ADDITION
          ? currentProblem.operand1 + currentProblem.operand2
          : currentProblem.operand1 - currentProblem.operand2
        ).toFixed(10)
      );

      const generateNewProblem = async (problemDifficulty: { level: string; difficulty: number }) : Promise<any> => {
        const newProblem = await getProblem(
          sdk,
          problemDifficulty.level,
          problemDifficulty.difficulty,
          currentOperation
        );
        return newProblem?.id === currentProblem.id ? await generateNewProblem(problemDifficulty) : newProblem
      }

      const saveUserLevelAndDifficulty = async (difficulty: number, level: string) => {
        onSetSettings({...settings, difficulty: difficulty, level: level});
        await sdk.getDataManager().saveSettings({data: { difficulty: difficulty, level: level }});
      };

      //compare
      if (convertAnswerString(answerString) === correctAnswer) {
        sdk.getAnalytics().push({
          event: "operation",
          action: "succes",
          id: currentProblem.id,
          level: currentProblem.level,
          difficulty: currentProblem.difficulty,
          language: intl.locale
        })
        tempCorrect += 1;

        setCorrectProblems(correctProblems.concat([currentProblem]));

        const problemDifficultyLevel = getProblemDifficulty(
          maxLevel,
          currentProblem.level,
          currentProblem.difficulty,
          "success",
          prevValidationStatus
        );
        saveUserLevelAndDifficulty(problemDifficultyLevel.difficulty, problemDifficultyLevel.level);
        updateAnnualProblemsList(currentProblem.id);
        updateSessionProblemsList(currentProblem.id);
        setCorrect(tempCorrect);
        if (problemIndex < problems.length && !wrongProblemsAdded) {
          //get next problem
          const targetProblem = await generateNewProblem(problemDifficultyLevel);
          if (targetProblem) {
            setNextProblem(targetProblem);
            setCurrentDifficulty(currentDifficulty + 1);
          }
          setPrevValidationStatus("success");
          setValidationStatus("success");
        }

        if (wrongProblemsAdded) {
          //compare and remove correct answered item from wrong word list
          setWrongProblems(
            wrongProblems.filter((problem) => {
              return problem.id !== currentProblem.id;
            })
          );
        }
      } else {
        sdk.getAnalytics().push({
          event: "operation",
          action: "erreur",
          id: currentProblem.id,
          level: currentProblem.level,
          difficulty: currentProblem.difficulty,
          language: intl.locale
        })
        setWrongProblems(
          wrongProblems.find(
            (wrongProblem) => wrongProblem.id === currentProblem.id
          )
            ? wrongProblems
            : wrongProblems.concat(currentProblem)
        );

        const problemDifficultyLevel = getProblemDifficulty(
          maxLevel,
          currentProblem.level,
          currentProblem.difficulty,
          "fail",
          prevValidationStatus
        );
        saveUserLevelAndDifficulty(problemDifficultyLevel.difficulty, problemDifficultyLevel.level);

        if (problemIndex < problems.length && !wrongProblemsAdded) {
          //get next problem
          const targetProblem = await generateNewProblem(problemDifficultyLevel);
          if (targetProblem) {
            setNextProblem(targetProblem);
            setCurrentDifficulty(currentDifficulty + 1);
          }
          setPrevValidationStatus("fail");
          setValidationStatus("fail");
        }
      }

      setProgress((tempStep + 1) / problemsCount * 100);
      currentStep < problemsCount && setCurrentStep(currentStep + 1);
    }

    setDisableValidate(true);
  };

  const onNext = async () => {
    let sstep = problemIndex + 1;

    setValidationStatus(undefined);

    //if current problem reaches to end of problems
    if (sstep >= problems.length) {
      sdk.getAnalytics().push({
        event: "serie",
        action: "complète",
        level: exerciseInfo?.level,
        operation: exerciseInfo?.operation,
        problemsLength: exerciseInfo?.problemsLength,
        language: intl.locale
      })
      sstep = 0;
      setProblemIndex(sstep);
      setResult(MAINSCREEN_RESULT_STEPS.CORRECTNESS);
      await updateUserStats(true);
    } else {
      if (nextProblem && !wrongProblemsAdded) {
        const stagedProblems = problems;
        stagedProblems[sstep] = nextProblem;
        setProblems(stagedProblems);
      }
      setProblemIndex(sstep);
      await updateUserStats(false);
    }
  };

  const handleExit = () => {
    setExitExercise(false);

    setInitial(true);
    setResult(MAINSCREEN_RESULT_STEPS.INITIAL);
    setProblems([]);

    clearAllData();
    sdk.getAnalytics().push({
      event: "serie",
      action: "quite",
      level: exerciseInfo?.level,
      operation: exerciseInfo?.operation,
      problemsLength: exerciseInfo?.problemsLength,
      language: intl.locale
    })
  };

  const handleQuit = () => {
    setResult(MAINSCREEN_RESULT_STEPS.RESULT);
  };

  const generateProblems = async (
    level: string,
    operation: OperatorModel,
    difficulty: number,
    problemsLength: number
  ) => {
    let stagedProblems = generateInitialProblems(problemsLength, operation);
    const firstProblem = await getProblem(
      sdk,
      level,
      difficulty,
      operation
    );
    if (firstProblem) {
      stagedProblems[0] = firstProblem;
    }

    return stagedProblems;
  }

  const onStart = async (
    level: string,
    difficulty: number,
    operation: OperatorModel,
    problemsLength: number
  ) => {
    if (sdk) {
      setInitial(false);

      setMaxLevel(level);
      setCurrentOperation(operation);
      setProblemsCount(problemsLength);
      let stagedProblems = generateInitialProblems(problemsLength, operation);
      const firstProblem = await getProblem(
        sdk,
        level,
        difficulty,
        operation
      );
      if (firstProblem) {
        stagedProblems[0] = firstProblem;
      }

      setProblems(await generateProblems(
        level || exerciseInfo?.level || TAGS_LIST[0].value,
        operation || exerciseInfo?.operation || OperatorModel.ADDITION,
        difficulty || INITIAL_DIFFICULTY,
        problemsLength || exerciseInfo?.problemsLength || 5
      ));
      setExerciseInfo({
        level: level,
        operation: operation,
        difficulty: difficulty,
        problemsLength: problemsLength
      })
      sdk.getAnalytics().push({
        event: "serie",
        action: "débute",
        level: level,
        operation: operation,
        problemsLength: problemsLength,
        language: intl.locale
      })
    }
  };

  const clearAllData = () => {
    setCurrentDifficulty(INITIAL_DIFFICULTY);
    setCorrect(0);
    setCorrectProblems([]);
    setWrongProblems([]);
    setNextProblem(undefined);
    setWrongProblemsAdded(false);
    setValidationStatus(undefined);
    setPrevValidationStatus(undefined);
    setCurrentStep(1);
    setProgress(0);
    setDisableValidate(false);
    setProblemIndex(0);
    setRestartMode(false);
  };

  const handleRestart = async () => {
    setProblems(await generateProblems(
      settings.level || exerciseInfo?.level || TAGS_LIST[0].value,
      exerciseInfo?.operation || OperatorModel.ADDITION,
      settings.difficulty || exerciseInfo?.difficulty || INITIAL_DIFFICULTY,
      exerciseInfo?.problemsLength || 5
    ));
    setResult(MAINSCREEN_RESULT_STEPS.INITIAL);

    clearAllData();
    setRestartMode(true);
  };

  const handleQuitComplete = () => {
    setCurrentStep(1);
    setProgress(0);
    setResult(MAINSCREEN_RESULT_STEPS.RESULT);
  };

  const updateUserStats = async (finalStep: boolean) => {
    try{
      if (isLoggedIn) {
        await sdk.getDataManager().saveSettings({
          data: {
            annualExerciseCount: annualExerciseCount + (finalStep ? 1 : 0),
            annualProblemsList: annualProblemsList,
          }
        });
        finalStep && setAnnualExerciseCount(prevState => prevState + 1);
      }
    } catch(err){
      console.log(err);
    }
    if (finalStep && !restartMode) {
      await sdk.getSessionManager().saveSessionData(
        {
          data: {
            sessionExerciseCount: sessionExerciseCount + (finalStep ? 1 : 0),
            sessionProblemsList: sessionProblemsList
          }
        }
      )
      setSessionExerciseCount(prevState => prevState + 1);
    }
  };

  return (
    <>
      <Profile
        sdk={sdk}
        sessionData={{
          sessionProblemsList: sessionProblemsList,
          sessionExerciseCount: sessionExerciseCount
        }}
      />
      <div className="content">
        {initial ? (
          <InitialStep sdk={sdk} onStart={onStart} demo={demo} level={maxLevel} />
        ) : (
          <DndProvider backend={isTouchDevice() ? TouchBackend : HTML5Backend}>
            {problems && problems.length > 0 && (
              <ExerciseScreen
                onValidte={onValidte}
                problems={problems}
                initialProblemsLength={problemsCount}
                problemIndex={problemIndex}
                handleQuit={() => setExitExercise(true)}
                progress={progress}
                currentStep={currentStep}
                correct={correct}
                setDisableValidate={setDisableValidate}
                disableValidate={disableValidate}
                onNext={onNext}
                validationStatus={validationStatus}
              />
            )}
          </DndProvider>
        )}

        <APCommonDialog
          open={exitExercise}
          title={intl.formatMessage({id: 'quit-exercise-title', defaultMessage: "Quitter l’exercice"})}
          onNext={handleExit}
          onClose={() => setExitExercise(false)}
          error={true}
        >
          <Box
            sx={{
              padding: isLargeScreen ? "46px 0" : "36px 0px",
              textAlign: "center",
            }}
          >
            <TextWithLineBreaks text={intl.formatMessage({
              id: 'quit-exercise-description',
              defaultMessage: `Es-tu sûr de vouloir quitter l’exercice? {nl}Ta progression sera sauvegardée.`
            })}/>
          </Box>
        </APCommonDialog>

        <APCorrectnessDialog
          open={result === MAINSCREEN_RESULT_STEPS.CORRECTNESS}
          onRestart={handleRestart}
          onQuit={handleQuitComplete}
          correctCount={correct}
          problemsCount={problemsCount}
        />
        <APResultDialog
          open={result === MAINSCREEN_RESULT_STEPS.RESULT}
          title={intl.formatMessage({id: 'bravo-heading', defaultMessage: `"Bravo!"`})}
          onNext={handleExit}
        >
          <Box
            sx={{
              padding: isLargeScreen ? "12px 26px" : "10px 20px",
              textAlign: "center",
              fontSize: isLargeScreen ? "1.25rem" : "1rem"
            }}
          >
            {
              intl.formatMessage({
                id: 'words-added-to-vocabulary-title',
                defaultMessage: `Tu as ajouté ${correct} mots à ton vocabulaire!`
              }).replace("{correct}", correct?.toString())
            }
          </Box>
          <Box
            sx={{
              padding: isLargeScreen ? "26px" : "20px",
              bg: "#fcfcfa",
              display: "flex",
              justifyContent: "center",
              marginTop: isLargeScreen ? "18px" : "15px",
            }}
          >
            <Box
              sx={{
                width: isLargeScreen ? "164px" : "130px",
                background: "#f5f3ef",
                textAlign: "center",
                borderRadius: "15px",
                padding: isLargeScreen ? "12px 0px 24px 0px" : "10px 0px 20px 0px",
                margin: isLargeScreen ? "0px 12px" : "0px 10px",
              }}
            >
              <Box
                sx={{
                  fontSize: isLargeScreen ? "36px" : "28px",
                  fontWeight: "bold",
                }}
              >
                {sessionProblemsList.length}
              </Box>
              <Box
                sx={{
                  fontSize: isLargeScreen ? "14px" : "11px",
                  lineHeight: isLargeScreen ? "12px" : "10px",
                }}
              >
                <FormattedMessage id="succeeded-session-operation-label" defaultMessage={"Questions réussies aujourd’hui"}/>
              </Box>
            </Box>
            <Box
              sx={{
                width: isLargeScreen ? "164px" : "130px",
                background: "#f5f3ef",
                textAlign: "center",
                borderRadius: "15px",
                padding: isLargeScreen ? "12px 0px 24px 0px" : "10px 0px 20px 0px",
                margin: isLargeScreen ? "0px 12px" : "0px 10px",
              }}
            >
              <Box
                sx={{
                  fontSize: isLargeScreen ? "36px" : "28px",
                  fontWeight: "bold",
                }}
              >
                {sessionExerciseCount}
              </Box>
              <Box
                sx={{
                  fontSize: isLargeScreen ? "14px" : "11px",
                  lineHeight: isLargeScreen ? "12px" : "10px",
                }}
              >
                <TextWithLineBreaks text={intl.formatMessage({
                  id: 'session-exercise-label',
                  defaultMessage: `Exercices complétés aujourd’hui`
                })}/>
              </Box>
            </Box>
          </Box>
          {isLoggedIn && (
            <Box
              sx={{
                padding: isLargeScreen ? "24px" : "20px",
                bg: "#fcfcfa",
                display: "flex",
                justifyContent: "center",
                marginTop: isLargeScreen ? "19px" : "15px",
              }}
            >
              <Box
                sx={{
                  width: isLargeScreen ? "164px" : "130px",
                  background: "#f5f3ef",
                  textAlign: "center",
                  borderRadius: "15px",
                  padding: isLargeScreen ? "12px 0px 24px 0px" : "10px 0px 20px 0px",
                  margin: isLargeScreen ? "0px 12px" : "0px 10px",
                }}
              >
                <Box
                  sx={{
                    fontSize: isLargeScreen ? "36px" : "28px",
                    fontWeight: "bold",
                  }}
                >
                  {annualProblemsList.length}
                </Box>
                <Box
                  sx={{
                    fontSize: isLargeScreen ? "14px" : "11px",
                    lineHeight: isLargeScreen ? "12px" : "10px",
                  }}
                >
                  <TextWithLineBreaks text={intl.formatMessage({
                    id: 'succeeded-overall-operation-label', defaultMessage: `Questions réussies depuis toujours`
                  })}/>
                </Box>
              </Box>

              <Box
                sx={{
                  width: isLargeScreen ? "164px" : "130px",
                  background: "#f5f3ef",
                  textAlign: "center",
                  borderRadius: "15px",
                  padding: isLargeScreen ? "12px 0px 24px 0px" : "10px 0px 20px 0px",
                  margin: isLargeScreen ? "0px 12px" : "0px 10px",
                }}
              >
                <Box
                  sx={{
                    fontSize: isLargeScreen ? "36px" : "28px",
                    fontWeight: "bold",
                  }}
                >
                  {annualExerciseCount}
                </Box>
                <Box
                  sx={{
                    fontSize: isLargeScreen ? "14px" : "11px",
                    lineHeight: isLargeScreen ? "12px" : "10px",
                  }}
                >
                  <TextWithLineBreaks text={intl.formatMessage({
                    id: 'overall-exercise-label', defaultMessage: `Exercices complétés depuis toujours`
                  })}/>
                </Box>
              </Box>
            </Box>
          )}
        </APResultDialog>
      </div>
    </>
  );
}

export default MainScreen;
